/**
 * 🚀 Seasonal Rates Hook - Primary pricing data source
 * Uses the new seasonal pricing architecture with get_rates_matrix RPC
 * Replaces legacy room_rates with identical API for seamless migration
 * 
 * 🔧 FIX: Мемоизация дат для предотвращения бесконечного цикла RPC-вызовов
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { useToast } from '@/hooks/use-toast';

export interface RateData {
  price: number;
  roomIds: string[];
  available: number;
  hasException?: boolean;
}

export interface RoomRatesData {
  [categoryId: string]: {
    [date: string]: RateData;
  };
}

interface RateUpdate {
  categoryId: string;
  date: string;
  newPrice: number;
}

interface UseSeasonalRatesReturn {
  rates: RoomRatesData;
  loading: boolean;
  updating: boolean;
  defaultRatePlanId: string | null;
  updateRate: (categoryId: string, date: string, newPrice: number) => Promise<void>;
  updateRatesBulk: (updates: RateUpdate[]) => Promise<number>;
  deleteRatesBulk: (items: Array<{ categoryId: string; date: string }>) => Promise<number>;
  refetch: () => Promise<void>;
}

export function useSeasonalRates(
  startDate: Date,
  endDate: Date,
  ratePlanId?: string | null
): UseSeasonalRatesReturn {
  const { currentHotel } = useOptimizedHotel();
  const { toast } = useToast();
  
  const [rates, setRates] = useState<RoomRatesData>({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [defaultRatePlanId, setDefaultRatePlanId] = useState<string | null>(null);
  const [roomsByCategory, setRoomsByCategory] = useState<Record<string, string[]>>({});

  const hotelId = currentHotel?.id;

  // Fetch default rate plan
  useEffect(() => {
    if (!hotelId) return;
    
    const fetchDefaultPlan = async () => {
      const { data } = await supabase
        .from('rate_plans')
        .select('id')
        .eq('hotel_id', hotelId)
        .eq('is_default', true)
        .eq('is_active', true)
        .limit(1)
        .single();
      
      if (data) {
        setDefaultRatePlanId(data.id);
      }
    };
    
    fetchDefaultPlan();
  }, [hotelId]);

  // Fetch rooms by category for availability calculation
  useEffect(() => {
    if (!hotelId) return;
    
    const fetchRooms = async () => {
      const { data: roomsData } = await supabase
        .from('rooms')
        .select('id, room_category_id')
        .eq('hotel_id', hotelId)
        .is('deleted_at', null);
      
      if (roomsData) {
        const grouped: Record<string, string[]> = {};
        for (const room of roomsData) {
          const catId = room.room_category_id;
          if (catId) {
            if (!grouped[catId]) {
              grouped[catId] = [];
            }
            grouped[catId].push(room.id);
          }
        }
        setRoomsByCategory(grouped);
      }
    };
    
    fetchRooms();
  }, [hotelId]);

  // 🎯 FIX: Стабильные строковые ключи для дат (год/месяц/день, НЕ миллисекунды!)
  const startStr = useMemo(() => {
    if (!startDate || !(startDate instanceof Date) || isNaN(startDate.getTime())) return '';
    return `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
  }, [
    startDate instanceof Date ? startDate.getFullYear() : 0,
    startDate instanceof Date ? startDate.getMonth() : 0,
    startDate instanceof Date ? startDate.getDate() : 0
  ]);
  
  // 🎯 FIX: endDate - 1 день (дата выезда НЕ тарифицируется, только ночи проживания)
  const adjustedEndDate = useMemo(() => {
    if (!endDate || !(endDate instanceof Date) || isNaN(endDate.getTime())) return null;
    const adjusted = new Date(endDate);
    adjusted.setDate(adjusted.getDate() - 1);
    return adjusted;
  }, [
    endDate instanceof Date ? endDate.getFullYear() : 0,
    endDate instanceof Date ? endDate.getMonth() : 0,
    endDate instanceof Date ? endDate.getDate() : 0
  ]);
  
  const endStr = useMemo(() => {
    if (!adjustedEndDate) return '';
    return `${adjustedEndDate.getFullYear()}-${String(adjustedEndDate.getMonth() + 1).padStart(2, '0')}-${String(adjustedEndDate.getDate()).padStart(2, '0')}`;
  }, [adjustedEndDate]);

  const activePlanId = ratePlanId || defaultRatePlanId;

  // 🎯 FIX: Ref для roomsByCategory чтобы избежать зависимости в fetchRates
  const roomsByCategoryRef = useRef(roomsByCategory);
  roomsByCategoryRef.current = roomsByCategory;

  // Main fetch function using RPC - roomsByCategory is accessed via ref to avoid infinite loop
  const fetchRates = useCallback(async () => {
    if (!hotelId || !activePlanId || !startStr || !endStr) return;
    
    setLoading(true);
    
    try {
      // Use RPC for massive performance gain
      const { data, error } = await supabase.rpc('get_rates_matrix', {
        p_hotel_id: hotelId,
        p_start_date: startStr,
        p_end_date: endStr,
        p_rate_plan_id: activePlanId
      });
      
      if (error) {
        console.error('Error fetching rates matrix:', error);
        toast({
          title: 'Ошибка загрузки цен',
          description: error.message,
          variant: 'destructive'
        });
        return;
      }
      
      // Transform RPC result to RoomRatesData format
      const ratesData: RoomRatesData = {};
      const currentRoomsByCategory = roomsByCategoryRef.current;
      
      const rows = data as Array<{ 
        category_id: string; 
        rate_date: string; 
        price: number; 
        has_exception: boolean 
      }> | null;
      
      if (rows && Array.isArray(rows)) {
        for (const row of rows) {
          const categoryId = row.category_id;
          const dateStr = row.rate_date;
          
          if (!ratesData[categoryId]) {
            ratesData[categoryId] = {};
          }
          
          ratesData[categoryId][dateStr] = {
            price: row.price || 0,
            roomIds: currentRoomsByCategory[categoryId] || [],
            available: (currentRoomsByCategory[categoryId] || []).length,
            hasException: row.has_exception
          };
        }
      }
      
      setRates(ratesData);
    } catch (err) {
      console.error('Error in fetchRates:', err);
    } finally {
      setLoading(false);
    }
  }, [hotelId, activePlanId, startStr, endStr, toast]);

  // 🎯 FIX: Refetch ТОЛЬКО при изменении стабильных ключей
  useEffect(() => {
    if (startStr && endStr) {
      fetchRates();
    }
  }, [fetchRates, startStr, endStr]);

  // Update single rate (writes to rate_exceptions)
  const updateRate = useCallback(async (
    categoryId: string,
    date: string,
    newPrice: number
  ) => {
    if (!hotelId) return;
    
    const activePlanId = ratePlanId || defaultRatePlanId;
    if (!activePlanId) return;
    
    setUpdating(true);
    
    try {
      // 🎯 АК-47: единая атомарная серверная команда с точечным пересчётом кэша
      const { data, error } = await supabase.rpc('save_rate_exceptions_bulk', {
        p_items: [{
          hotel_id: hotelId,
          category_id: categoryId,
          rate_plan_id: activePlanId,
          exception_date: date,
          override_price: newPrice,
          reason: 'Manual override'
        }]
      });

      if (error) throw error;
      const saved = (data as { saved?: number } | null)?.saved ?? 0;
      if (saved !== 1) throw new Error(`Сервер подтвердил ${saved} записей вместо 1`);
      
      // Optimistic update
      setRates(prev => ({
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          [date]: {
            ...prev[categoryId]?.[date],
            price: newPrice,
            hasException: true
          }
        }
      }));
      
      toast({
        title: 'Цена обновлена',
        description: `${newPrice.toLocaleString('ru-RU')} ₽`
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error updating rate:', err);
      toast({
        title: 'Ошибка',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setUpdating(false);
    }
  }, [hotelId, ratePlanId, defaultRatePlanId, toast]);

  // Bulk update rates — атомарная серверная команда, дедупликация, чанки по 2000
  // Возвращает фактически сохранённое сервером число ячеек. При ошибке — выбрасывает исключение.
  const updateRatesBulk = useCallback(async (updates: RateUpdate[]): Promise<number> => {
    if (!hotelId || updates.length === 0) return 0;

    const activePlanId = ratePlanId || defaultRatePlanId;
    if (!activePlanId) return 0;

    // 🎯 АК-47: схлопываем дубли ячеек до отправки
    const seen = new Set<string>();
    const deduped: RateUpdate[] = [];
    for (const u of updates) {
      const key = `${u.categoryId}|${u.date}`;
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(u);
    }

    setUpdating(true);

    try {
      const CHUNK_SIZE = 2000;
      let totalSaved = 0;

      for (let i = 0; i < deduped.length; i += CHUNK_SIZE) {
        const chunk = deduped.slice(i, i + CHUNK_SIZE).map(u => ({
          hotel_id: hotelId,
          category_id: u.categoryId,
          rate_plan_id: activePlanId,
          exception_date: u.date,
          override_price: u.newPrice,
          reason: 'Bulk update'
        }));

        const { data, error } = await supabase.rpc('save_rate_exceptions_bulk', {
          p_items: chunk
        });

        if (error) throw error;
        const saved = (data as { saved?: number } | null)?.saved ?? 0;
        if (saved !== chunk.length) {
          throw new Error(`Сервер сохранил ${saved} из ${chunk.length} ячеек`);
        }
        totalSaved += saved;
      }

      // Optimistic update по подтверждённым сервером данным
      setRates(prev => {
        const newRates = { ...prev };
        for (const u of deduped) {
          if (!newRates[u.categoryId]) {
            newRates[u.categoryId] = {};
          }
          newRates[u.categoryId][u.date] = {
            ...newRates[u.categoryId]?.[u.date],
            price: u.newPrice,
            hasException: true
          };
        }
        return newRates;
      });

      // Контрольное обновление календаря с сервера
      fetchRates();

      return totalSaved;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error bulk updating rates:', err);
      toast({
        title: 'Ошибка',
        description: errorMessage,
        variant: 'destructive'
      });
      throw err;
    } finally {
      setUpdating(false);
    }
  }, [hotelId, ratePlanId, defaultRatePlanId, toast, fetchRates]);

  // Bulk delete priority prices (rate_exceptions) — returns count of deleted rows
  const deleteRatesBulk = useCallback(async (items: Array<{ categoryId: string; date: string }>) => {
    if (!hotelId || items.length === 0) return 0;
    
    const activePlanId = ratePlanId || defaultRatePlanId;
    if (!activePlanId) return 0;
    
    setUpdating(true);
    
    try {
      // 🎯 АК-47: дедупликация ячеек до отправки
      const seen = new Set<string>();
      const deduped: Array<{ categoryId: string; date: string }> = [];
      for (const it of items) {
        const key = `${it.categoryId}|${it.date}`;
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(it);
      }

      const CHUNK_SIZE = 2000;
      let totalDeleted = 0;

      for (let i = 0; i < deduped.length; i += CHUNK_SIZE) {
        const chunk = deduped.slice(i, i + CHUNK_SIZE).map(it => ({
          hotel_id: hotelId,
          rate_plan_id: activePlanId,
          category_id: it.categoryId,
          exception_date: it.date
        }));

        const { data, error } = await supabase.rpc('delete_rate_exceptions_bulk', {
          p_items: chunk
        });

        if (error) throw error;
        totalDeleted += (data as { deleted?: number } | null)?.deleted ?? 0;
      }

      // Optimistic update: clear hasException and re-fetch to restore seasonal price
      setRates(prev => {
        const newRates = { ...prev };
        for (const item of deduped) {
          if (newRates[item.categoryId]?.[item.date]) {
            newRates[item.categoryId] = { ...newRates[item.categoryId] };
            newRates[item.categoryId][item.date] = {
              ...newRates[item.categoryId][item.date],
              hasException: false
            };
          }
        }
        return newRates;
      });

      // Refetch to restore actual seasonal prices
      await fetchRates();

      return totalDeleted;

    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error bulk deleting rates:', err);
      toast({
        title: 'Ошибка',
        description: errorMessage,
        variant: 'destructive'
      });
      throw err;
    } finally {
      setUpdating(false);
    }
  }, [hotelId, ratePlanId, defaultRatePlanId, toast, fetchRates]);

  return useMemo(() => ({
    rates,
    loading,
    updating,
    defaultRatePlanId,
    updateRate,
    updateRatesBulk,
    deleteRatesBulk,
    refetch: fetchRates
  }), [rates, loading, updating, defaultRatePlanId, updateRate, updateRatesBulk, deleteRatesBulk, fetchRates]);
}
